import React, { useState, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Question,
  Robot,
  UserCircle,
  PencilSimple,
  CheckCircle,
  ClockCounterClockwise,
  File,
  ArrowSquareOut,
  Textbox,
} from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  DueDiligenceQuestion,
  DueDiligenceResponse,
  DueDiligenceResponseSourceType,
  DueDiligenceResponseHistory,
  DueDiligenceAnswerType,
  statusColorToClasses,
  responseSourceTypeLabels,
} from 'shared/types/due-diligence';
import DueDiligenceHistoryPanel from './DueDiligenceHistoryPanel';
import DueDiligenceAuditTrail from './DueDiligenceAuditTrail';

interface ResponseState {
  questionId: number;
  selectedOptionId: number | null;
  notes: string;
  freeTextAnswer?: string | null;
  numberAnswer?: number | null;
  dateAnswer?: string | null;
  booleanAnswer?: boolean | null;
}

interface DueDiligenceQuestionItemProps {
  question: DueDiligenceQuestion;
  responseState: ResponseState | undefined;
  existingResponse: DueDiligenceResponse | undefined;
  onResponseChange: (questionId: number, updates: Partial<ResponseState>) => void;
  getUserName: (userId: string | undefined) => string | null;
  buildingId?: string;
  unitId?: string;
}

/**
 * Returns source icon with tooltip for audit trail
 */
const getSourceIcon = (response: DueDiligenceResponse | undefined) => {
  if (!response?.sourceType) return null;

  if (response.sourceType === DueDiligenceResponseSourceType.AI_EXTRACTED) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span><Robot weight="duotone" size={14} className="text-blue-500" /></span>
          </TooltipTrigger>
          <TooltipContent>AI geëxtraheerd (dropzone)</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (response.sourceType === DueDiligenceResponseSourceType.AI_VALIDATED) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span><Robot weight="duotone" size={14} className="text-green-500" /></span>
          </TooltipTrigger>
          <TooltipContent>AI geëxtraheerd, gevalideerd door gebruiker</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (response.sourceType === DueDiligenceResponseSourceType.AI_EDITED) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span><PencilSimple weight="duotone" size={14} className="text-orange-500" /></span>
          </TooltipTrigger>
          <TooltipContent>AI geëxtraheerd, bewerkt door gebruiker</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (response.sourceType === DueDiligenceResponseSourceType.MANUAL) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span><UserCircle weight="duotone" size={14} className="text-neutral-secondary-mid" /></span>
          </TooltipTrigger>
          <TooltipContent>Handmatig ingevoerd</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return null;
};

/**
 * Component for rendering a single DD question with answer input.
 * Supports OPTIONS, EXTRACTION, FILE_BASED, and DOCUMENT_EXISTS question types.
 */
const DueDiligenceQuestionItem: React.FC<DueDiligenceQuestionItemProps> = ({
  question,
  responseState,
  existingResponse,
  onResponseChange,
  getUserName,
  buildingId,
  unitId,
}) => {
  const { session } = useSession();
  const [expandedAudit, setExpandedAudit] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(false);
  const [historyData, setHistoryData] = useState<DueDiligenceResponseHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Get status color from selected option
  const statusColor = responseState?.selectedOptionId
    ? question.answerOptions?.find(opt => opt.id === responseState.selectedOptionId)?.statusColor
    : null;

  const sourceIcon = getSourceIcon(existingResponse);
  const hasAuditDetails = (existingResponse?.conditionEvaluations?.optionEvaluations?.length ?? 0) > 0;

  // Fetch history for this question
  const fetchQuestionHistory = useCallback(async () => {
    if (!session || loadingHistory) return;

    // Don't refetch if we already have data
    if (historyData.length > 0) return;

    setLoadingHistory(true);
    try {
      const endpoint = unitId
        ? `${API_URL}/due-diligence-responses/history/unit/${unitId}/question/${question.id}`
        : `${API_URL}/due-diligence-responses/history/building/${buildingId}/question/${question.id}`;

      const response = await fetchWithAuth(endpoint, { method: 'GET' }, session);

      if (!response.ok) {
        throw new Error('Failed to fetch history');
      }

      const data = await response.json();
      setHistoryData(data);
    } catch (error) {
      console.error('Error fetching question history:', error);
      toast.error('Kon geschiedenis niet laden');
    } finally {
      setLoadingHistory(false);
    }
  }, [session, buildingId, unitId, question.id, loadingHistory, historyData.length]);

  // Toggle history section
  const toggleHistory = useCallback(() => {
    if (expandedHistory) {
      setExpandedHistory(false);
    } else {
      setExpandedHistory(true);
      fetchQuestionHistory();
    }
  }, [expandedHistory, fetchQuestionHistory]);

  return (
    <div className="p-4 border rounded-lg bg-white shadow-sm space-y-3">
      {/* Question header */}
      <div className="flex items-start gap-2">
        <Question weight="duotone" size={18} className="text-neutral-secondary-mid mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-sm font-medium">{question.displayName}</Label>
            {/* Source icon for audit trail */}
            {sourceIcon}
            {/* Show audit details button if available */}
            {hasAuditDetails && (
              <button
                onClick={() => setExpandedAudit(!expandedAudit)}
                className="text-xs text-blue-600 hover:underline"
              >
                {expandedAudit ? 'Verberg AI details' : 'Toon AI details'}
              </button>
            )}
            {/* History toggle button */}
            {existingResponse && (
              <button
                onClick={toggleHistory}
                className="text-xs text-violet-600 hover:underline flex items-center gap-0.5"
              >
                <ClockCounterClockwise size={12} />
                {expandedHistory ? 'Verberg' : 'Toon'} geschiedenis
              </button>
            )}
          </div>
          {question.helpText && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {question.helpText}
            </p>
          )}

          {/* Audit info line */}
          {existingResponse && (
            <div className="text-xs text-neutral-secondary-mid mt-1 space-y-1">
              {/* Source type and validation info */}
              <p>
                {existingResponse.sourceType && responseSourceTypeLabels[existingResponse.sourceType]}
                {existingResponse.aiConfidence !== undefined && (
                  <span className="ml-2">AI zekerheid: {Math.round(existingResponse.aiConfidence * 100)}%</span>
                )}
                {existingResponse.updatedAt && (
                  <span className="ml-2">{new Date(existingResponse.updatedAt).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}</span>
                )}
              </p>

              {/* Validator info for AI results */}
              {(existingResponse.sourceType === DueDiligenceResponseSourceType.AI_EXTRACTED ||
                existingResponse.sourceType === DueDiligenceResponseSourceType.AI_VALIDATED ||
                existingResponse.sourceType === DueDiligenceResponseSourceType.AI_EDITED) &&
                existingResponse.validatedBy && (
                <p className="text-green-600">
                  <CheckCircle weight="fill" size={12} className="inline mr-1" />
                  Gevalideerd door {getUserName(existingResponse.validatedBy)}
                  {existingResponse.validatedAt && (
                    <span className="text-neutral-secondary-mid ml-1">
                      op {new Date(existingResponse.validatedAt).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  )}
                </p>
              )}

              {/* Source files display */}
              {existingResponse.sourceFileLogs && existingResponse.sourceFileLogs.length > 1 ? (
                <div className="mt-2 p-2 bg-violet-50 rounded-md border border-violet-100">
                  <div className="flex items-center gap-1.5 mb-2">
                    <File weight="duotone" size={14} className="text-violet-600" />
                    <span className="text-xs font-medium text-violet-800">
                      Gekoppelde documenten ({existingResponse.sourceFileLogs.length})
                    </span>
                  </div>
                  <div className="space-y-2">
                    {existingResponse.sourceFileLogs.map((file) => (
                      <div key={file.id} className="flex items-center gap-2 text-xs bg-white rounded p-1.5">
                        <File size={12} className="text-violet-500 flex-shrink-0" />
                        {file.sharePointFileUrl ? (
                          <a
                            href={file.sharePointFileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-violet-600 hover:underline inline-flex items-center gap-0.5 font-medium truncate"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {file.fileName || file.label || `Document #${file.id}`}
                            <ArrowSquareOut size={10} />
                          </a>
                        ) : (
                          <span className="font-medium truncate">{file.fileName || file.label || `Document #${file.id}`}</span>
                        )}
                        {file.documentDate && (
                          <span className="text-muted-foreground ml-auto flex-shrink-0">
                            {new Date(file.documentDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : existingResponse.sourceFileLog && (
                <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-100">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <File weight="duotone" size={14} className="text-blue-600" />
                    <span className="text-xs font-medium text-blue-800">Gekoppeld document</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    {existingResponse.sourceFileLog.sharePointFileUrl ? (
                      <a
                        href={existingResponse.sourceFileLog.sharePointFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline inline-flex items-center gap-0.5 font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {existingResponse.sourceFileLog.fileName || existingResponse.sourceFileLog.label || `Document #${existingResponse.sourceFileLog.id}`}
                        <ArrowSquareOut size={10} />
                      </a>
                    ) : (
                      <span className="font-medium">
                        {existingResponse.sourceFileLog.fileName || existingResponse.sourceFileLog.label || `Document #${existingResponse.sourceFileLog.id}`}
                      </span>
                    )}
                  </div>
                  {/* Document and expiration dates */}
                  <div className="flex flex-wrap gap-3 mt-2 text-xs">
                    {existingResponse.sourceFileLog.documentDate && (
                      <div className="flex items-center gap-1.5 text-blue-700">
                        <span className="font-medium">Documentdatum:</span>
                        <span>{new Date(existingResponse.sourceFileLog.documentDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </div>
                    )}
                    {existingResponse.sourceFileLog.expirationDate && (
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-orange-700">Vervaldatum:</span>
                        <span className={`${new Date(existingResponse.sourceFileLog.expirationDate) < new Date() ? 'text-red-600 font-semibold' : 'text-orange-600'}`}>
                          {new Date(existingResponse.sourceFileLog.expirationDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {new Date(existingResponse.sourceFileLog.expirationDate) < new Date() && ' (verlopen)'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Status indicator */}
        {statusColor && (
          <div className={`w-4 h-4 rounded-full ${statusColorToClasses[statusColor]}`} />
        )}
      </div>

      {/* Expanded AI Audit Trail */}
      {expandedAudit && existingResponse?.conditionEvaluations && (
        <DueDiligenceAuditTrail conditionEvaluations={existingResponse.conditionEvaluations} />
      )}

      {/* Expanded History */}
      {expandedHistory && (
        <div className="bg-violet-50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2 text-violet-700">
            <ClockCounterClockwise weight="duotone" size={16} />
            <span className="text-sm font-medium">Wijzigingsgeschiedenis</span>
          </div>
          <DueDiligenceHistoryPanel
            history={historyData}
            isLoading={loadingHistory}
            getUserName={getUserName}
          />
        </div>
      )}

      {/* Answer input - depends on question type */}
      <div className="pl-6">
        {question.answerType === DueDiligenceAnswerType.EXTRACTION ? (
          // EXTRACTION questions: show input based on extractionValueType
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-violet-600 mb-1">
              <Textbox size={14} />
              <span>Extractie waarde ({
                question.extractionValueType === 'number' ? 'getal' :
                question.extractionValueType === 'date' ? 'datum' :
                question.extractionValueType === 'boolean' ? 'ja/nee' : 'tekst'
              })</span>
            </div>
            {question.extractionValueType === 'number' ? (
              <Input
                type="number"
                placeholder="Voer getal in..."
                value={responseState?.numberAnswer ?? ''}
                onChange={(e) =>
                  onResponseChange(question.id, {
                    numberAnswer: e.target.value ? parseFloat(e.target.value) : null,
                  })
                }
                className="text-sm"
              />
            ) : question.extractionValueType === 'date' ? (
              <Input
                type="date"
                value={responseState?.dateAnswer ?? ''}
                onChange={(e) =>
                  onResponseChange(question.id, {
                    dateAnswer: e.target.value || null,
                  })
                }
                className="text-sm"
              />
            ) : question.extractionValueType === 'boolean' ? (
              <Select
                value={responseState?.booleanAnswer === true ? 'true' : responseState?.booleanAnswer === false ? 'false' : ''}
                onValueChange={(value) =>
                  onResponseChange(question.id, {
                    booleanAnswer: value === 'true' ? true : value === 'false' ? false : null,
                  })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecteer ja/nee..." />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4} align="start" className="max-h-[200px]" {...{ modal: false } as any}>
                  <SelectItem value="true">Ja</SelectItem>
                  <SelectItem value="false">Nee</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              // Default to textarea for string/text extraction
              <Textarea
                placeholder="Voer geëxtraheerde waarde in..."
                value={responseState?.freeTextAnswer ?? ''}
                onChange={(e) =>
                  onResponseChange(question.id, {
                    freeTextAnswer: e.target.value || null,
                  })
                }
                rows={3}
                className="text-sm"
              />
            )}
          </div>
        ) : (
          // OPTIONS questions: show dropdown
          <Select
            value={responseState?.selectedOptionId?.toString() ?? ''}
            onValueChange={(value) =>
              onResponseChange(question.id, {
                selectedOptionId: value ? parseInt(value) : null,
              })
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecteer een antwoord..." />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={4} align="start" className="max-h-[200px]" {...{ modal: false } as any}>
              {question.answerOptions
                ?.filter(opt => opt.active)
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((option) => (
                  <SelectItem key={option.id} value={option.id.toString()}>
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${statusColorToClasses[option.statusColor]}`} />
                      <span>{option.label}</span>
                    </div>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Notes */}
      <div className="pl-6">
        <Textarea
          placeholder="Opmerkingen (optioneel)..."
          value={responseState?.notes ?? ''}
          onChange={(e) => onResponseChange(question.id, { notes: e.target.value })}
          rows={2}
          className="text-sm"
        />
      </div>
    </div>
  );
};

export default DueDiligenceQuestionItem;
